import { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useScreen } from '@allie/mfe';
import { HeaderProps } from '@allie/app-ui-lib';
import { useAllieRouting, useAllieAuth, useAppTranslations, useFeatureFlag } from '../../../hooks';
import { GET_LOGGED_USER, UPDATE_USER } from '../../../data';
import { config } from '../../../helpers/configs';
import { AppContext } from '../../../context';
import { getLang } from '../../../helpers';
import { useNotification } from '../../../context/NotificationProvider';

export const useHeader = (setChangingLang: any) => {
  const [loginOpen, setLoginOpen] = useState<boolean | undefined>(false);
  const [currentDate, setCurrentDate] = useState(dayjs());
  const { isMobile } = useScreen();
  const { getDefaultPage } = useAllieRouting();
  const { isAuthenticated, login, loggedUser, logout, openLogin } = useAllieAuth();
  const { hasNotification, unseenAlertsTotal } = useNotification();
  const { setLang } = useContext(AppContext);
  const {
    HELP_LABEL,
    LOGIN,
    LOGIN_MESSAGE,
    LOGIN_MESSAGE_MOBILE,
    USER_MENU_CHANGE_ACCOUNT,
    USER_MENU_LOGOUT,
    SELECT_LANGUAGE,
    ALERTS,
    MENU_CONFIGURATIONS,
  } = useAppTranslations();
  const [updateUser] = useMutation(UPDATE_USER);
  const [getLoggedUserQuery] = useLazyQuery(GET_LOGGED_USER, {
    fetchPolicy: 'network-only',
  });
  const { updateLoggedUser } = useAllieAuth();

  useEffect(() => {
    const interval = setInterval(() => setCurrentDate(dayjs()), 1000 * 30);
    return () => clearInterval(interval);
  }, []);

  const lang = useMemo(() => getLang(loggedUser, config.LANG), [loggedUser]);

  const getCurrentTime = useCallback(
    () => currentDate.format('hh:mm a, dddd DD-MM-YYYY'),
    [currentDate]
  );

  const headerProps: HeaderProps = {
    isAuthenticated: isAuthenticated || false,
    loggedUser: {
      name: loggedUser?.name || '',
      avatar: loggedUser?.avatar,
    },
    ...(isMobile && { color: 'black-90' }),
    optionsMenuAvatar: [
      {
        label: USER_MENU_CHANGE_ACCOUNT,
        onClick: () => {
          setLoginOpen(true);
          login(true, () => {
            setLoginOpen(false);
          });
        },
        icon: 'peopleOutline',
      },
      {
        label: MENU_CONFIGURATIONS.MENU,
        onClick: () => {
          window.location.href = '/notifications';
        },
        icon: 'settingsOutline',
      },
      {
        label: USER_MENU_LOGOUT,
        onClick: () => logout(),
        icon: 'logOutOutline',
      },
    ],
    onClickLogin: () => {
      setLoginOpen(true);
      login(true, () => {
        setLoginOpen(false);
      });
    },
    textLogin: LOGIN,
    messageLogin: isMobile ? LOGIN_MESSAGE_MOBILE : LOGIN_MESSAGE,
    currentTime: getCurrentTime(),
    imgLogo: config.TEST_ENV ? 'logo-test.png' : 'logo-prod.svg',
    defaultPage: getDefaultPage(),
    showHelp: true,
    helpLabel: HELP_LABEL,
    currentLanguage: loggedUser!.preferedLanguage,
    languageLabel: SELECT_LANGUAGE,
    languages: [
      { code: 'es', name: 'Español - ES' },
      { code: 'en', name: 'English - EN' },
    ],
    onLanguageChange: (preferedLanguage: string) => {
      setChangingLang(true);
      updateUser({
        variables: { updateUserInput: { id: loggedUser!.id, preferedLanguage } } }
      )
        .then(async () => {
          const { data } = await getLoggedUserQuery();
          updateLoggedUser(
            data.loggedUser,
            () => {
              setLang?.(preferedLanguage.toUpperCase());
          });
        })
        .finally(() => setChangingLang(false));
    },
    hasNotification,
    alerts: {
      label: ALERTS,
      unseenAlertsTotal: unseenAlertsTotal,
      href: '/factory-gpt/alerts',
    },
  };

  return {
    headerProps,
    loginOpen,
    setLoginOpen,
    lang,
    openLogin,
  };
};
